import { DestroyRef, Directive, effect, ElementRef, inject, input, signal } from "@angular/core";
import { InteractivityChecker } from "@angular/cdk/a11y";
import { LgGlobalShortcutsService } from "./lg-global-shortcuts.service";
import { getFirstTabbableElementAfter } from "@logex/framework/ui-core";

@Directive({
    selector: "[lgGlobalShortcutFocus]",
    standalone: true
})
export class LgGlobalShortcutFocusDirective {
    readonly name = input.required<string>({ alias: "lgGlobalShortcutFocus" });
    readonly ctrl = input(false);
    readonly shift = input(false);
    readonly alt = input(false);
    readonly key = input<string | undefined>();

    private _shortcutService = inject(LgGlobalShortcutsService);
    private _element = inject(ElementRef<HTMLElement>);
    private _interactivityChecker = inject(InteractivityChecker);

    constructor() {
        let unregister: (() => void) | null = null;
        effect(() => {
            unregister?.();
            const name = this.name();
            if (this._shortcutService.isStandardComponent(name)) {
                if (this.key() !== undefined) {
                    throw new Error(
                        `lgGlobalShortcutFocus with standard name "${name}" cannot have a key defined.`
                    );
                }
                unregister = this._shortcutService.registerStandardComponent(name, () =>
                    this._doFocus()
                );
            } else {
                if (this.key() == null || this.key() === "") {
                    throw new Error(`lgGlobalShortcutFocus with name "${name}" must define a key.`);
                }
                unregister = this._shortcutService.registerShortcut(
                    {
                        key: this.key()!,
                        ctrlKey: this.ctrl(),
                        shiftKey: this.shift(),
                        altKey: this.alt()
                    },
                    () => this._doFocus(),
                    name
                );
            }
        });
        inject(DestroyRef).onDestroy(() => {
            unregister?.();
        });
    }

    private _doFocus(): void {
        let searchRoot = this._element.nativeElement;
        if (this._element.nativeElement.tagName === "ROUTER-OUTLET") {
            searchRoot = this._element.nativeElement.nextElementSibling;
            if (!searchRoot) return;
        }
        const target = getFirstTabbableElementAfter(
            searchRoot,
            null,
            { value: true },
            false,
            this._interactivityChecker
        );
        target?.blur();
        target?.focus({ focusVisible: true } as any); // part of standard now, though Chrome doesn't yet support it
    }
}
